import { useMemo } from 'react';
import { MutationTuple, QueryResult } from '@apollo/client/react/types/types';
import { useMutation, useQuery, MutationUpdaterFn } from '@apollo/client';
import { GET_WEBHOOKS, CREATE_WEBHOOK, UPDATE_WEBHOOK, REMOVE_WEBHOOK, WEBHOOKS_FIELDS } from './queries';
import { IApiWebhook } from './types';
import { WebhookEventType } from '@tendium/prom-types';

export function useLoadWebhooks(): QueryResult<{ getWebhooks: IApiWebhook[] }> {
  return useQuery<{ getWebhooks: IApiWebhook[] }>(GET_WEBHOOKS);
}

export function useLoadWebhook(id: string): IApiWebhook | undefined {
  const { data } = useLoadWebhooks();
  const webhooks = data?.getWebhooks;
  return useMemo(() => {
    return webhooks && webhooks.find(hook => hook.id === id);
  }, [id, webhooks]);
}

export interface ICreateWebhook {
  __typename: 'Mutation';
  createWebhook: IApiWebhook & {
    __typename: 'Webhook';
  };
}

export function useCreateWebhook(): MutationTuple<
  ICreateWebhook,
  { name: string; url: string; eventType: WebhookEventType; secret: string }
> {
  return useMutation(CREATE_WEBHOOK);
}

export function getUpdateCacheOnWebhookCreate(): MutationUpdaterFn<{
  createWebhook: IApiWebhook;
  __typename: 'Mutation';
}> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const webhooksQueryData = cache.readQuery<{ getWebhooks: IApiWebhook[] } | null>({
      query: GET_WEBHOOKS
    });
    if (!webhooksQueryData) {
      return;
    }
    const { getWebhooks } = webhooksQueryData;
    cache.writeQuery({
      query: GET_WEBHOOKS,
      data: { getWebhooks: [...getWebhooks, data.createWebhook] }
    });
  };
}

export interface IUpdateWebhook {
  __typename: 'Mutation';
  updateWebhook: IApiWebhook & {
    __typename: 'Webhook';
  };
}
export interface IUpdateWebhookInput {
  name?: string;
  url?: string;
  eventType?: WebhookEventType;
  secret?: string;
}
export function useUpdateWebhook(): MutationTuple<
  IUpdateWebhook,
  { id: string; name?: string; url?: string; eventType?: WebhookEventType; secret?: string }
> {
  return useMutation(UPDATE_WEBHOOK);
}
export function getUpdateCacheOnUpdateWebhook(id: string): MutationUpdaterFn<IUpdateWebhook> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const webhookRef = cache.identify({
      __typename: 'Webhook',
      id
    });
    cache.writeFragment({
      id: webhookRef,
      fragment: WEBHOOKS_FIELDS,
      data: data.updateWebhook,
      fragmentName: 'webhooksFields'
    });
  };
}

export function useRemoveWebhook(): MutationTuple<
  { removeWebhook: IApiWebhook[]; __typename: 'Mutation' },
  { id: string }
> {
  return useMutation(REMOVE_WEBHOOK);
}

export function getUpdateCacheOnRemoveWebhook(
  id: string
): MutationUpdaterFn<{ removeWebhook: IApiWebhook[]; __typename: 'Mutation' }> {
  return (cache, { data }) => {
    if (!data) {
      return;
    }
    const webhooksData = cache.readQuery<{ getWebhooks: IApiWebhook[] } | null>({
      query: GET_WEBHOOKS
    });
    if (!webhooksData) {
      return;
    }
    const { getWebhooks } = webhooksData;
    const newWebhooks = getWebhooks.filter(hook => hook.id !== id);
    cache.writeQuery({
      query: GET_WEBHOOKS,
      data: { getWebhooks: newWebhooks }
    });
  };
}
